import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { customerApi } from '../services/api';
import type { Customer } from '../types';
import { bankNamesText } from '../components/CustomerBanks';

const DEFAULT_COLUMN_ORDER = [
  'customerNumber', 'name', 'email', 'phone', 'address', 'postalCode', 'city', 'banks', 'notes', 'verifiedCustomer'
];

/** The stored status code values, in the order the status filter lists them. */
const STATUS_VALUES = ['Delad', 'Verifierad'] as const;

/**
 * The contractor's clients, in the same list table as the projects
 * (ClickUp 869fj439m): search and filter per column, sorting, paging, saved
 * column order and cards on phones.
 */
export function CustomerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // The rows the table shows, for the export. A ref, not state: reporting them
  // must not re-render the page, or a new column list each render would loop.
  const visibleRows = useRef<Customer[]>([]);
  const setVisibleRows = useCallback((rows: Customer[]) => { visibleRows.current = rows; }, []);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  // Same saved preference key as before the shared table, so a saved order survives.
  const [columnOrder, handleColumnReorder] = useColumnOrder('customerListColumnOrder', DEFAULT_COLUMN_ORDER);

  useEffect(() => {
    customerApi.getCustomers()
      .then(setCustomers)
      .catch(error => console.error('Failed to load customers:', error))
      .finally(() => setIsLoading(false));
  }, []);

  const statusLabel = (value: string) =>
    value === 'Verifierad' ? t('customers.status.verified') : t('customers.status.shared');

  const statusBadge = (customer: Customer, className = '') => (
    <span className={`badge ${customer.verifiedCustomer === 'Verifierad' ? 'bg-success' : 'bg-warning text-dark'} ${className}`.trim()}>
      {statusLabel(customer.verifiedCustomer)}
    </span>
  );

  const statusOptions = useMemo(
    () => STATUS_VALUES.map(value => ({ value, label: statusLabel(value) })),
    // statusLabel only depends on the language (t).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  // Memoized: the table reports its visible rows whenever the columns change.
  const columns: ProjectsListColumn<Customer>[] = useMemo(() => [
    { field: 'customerNumber', label: t('customers.columns.customerNumber'), render: c => c.customerNumber },
    { field: 'name', label: t('customers.columns.name'), render: c => c.name },
    { field: 'email', label: t('customers.columns.email'), render: c => c.email },
    { field: 'phone', label: t('customers.columns.phone'), render: c => c.phone },
    { field: 'address', label: t('customers.columns.address'), render: c => c.address },
    { field: 'postalCode', label: t('customers.columns.postalCode'), render: c => c.postalCode },
    { field: 'city', label: t('customers.columns.city'), render: c => c.city },
    // The Beställare's banks, read-only (ClickUp 869eu6fpm).
    { field: 'banks', label: t('customers.columns.banks'), sortValue: c => bankNamesText(t, c.banks), filterValue: c => bankNamesText(t, c.banks), render: c => bankNamesText(t, c.banks) },
    { field: 'notes', label: t('customers.columns.notes'), render: c => c.notes },
    {
      field: 'verifiedCustomer',
      label: t('customers.columns.verified'),
      filter: 'select',
      selectAllLabel: t('customers.allStatuses'),
      selectOptions: statusOptions,
      sortValue: c => statusLabel(c.verifiedCustomer),
      render: c => statusBadge(c)
    }
    // The renderers only depend on the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [statusOptions, t]);

  // ?verifiedCustomer= pre-filters, as before.
  const initialFilters = useMemo<Record<string, string>>(() => {
    const verified = searchParams.get('verifiedCustomer');
    const init: Record<string, string> = {};
    if (verified) init.verifiedCustomer = verified;
    return init;
  }, [searchParams]);

  const openCustomer = (customer: Customer) => navigate(`/customers/${customer.id}`);
  const selectedCustomer = customers.find(customer => customer.id === selectedCustomerId);
  const toggleSelected = (customer: Customer) =>
    setSelectedCustomerId(previous => previous === customer.id ? null : customer.id);

  const exportToExcel = () => {
    const byField = new Map(columns.map(column => [column.field, column]));
    const order = columnOrder.filter(field => byField.has(field));
    const headers = order.map(field => byField.get(field)!.label);
    const cellValue = (customer: Customer, field: string): string => {
      if (field === 'verifiedCustomer') return statusLabel(customer.verifiedCustomer);
      if (field === 'banks') return bankNamesText(t, customer.banks);
      const value = (customer as unknown as Record<string, unknown>)[field];
      return value == null ? '' : String(value);
    };
    const rows = visibleRows.current.map(customer => order.map(field => cellValue(customer, field)));
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    // A BOM so Excel reads UTF-8.
    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const renderMobileCard = (customer: Customer, { open, selected }: { open: () => void; selected: boolean }) => (
    <div className={`card mb-2 admin-list-card ${selected ? 'border-primary' : ''}`}
      onClick={() => toggleSelected(customer)} onDoubleClick={open}>
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1 admin-list-card__body">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate">#{customer.customerNumber} {customer.name}</span>
              {statusBadge(customer, 'flex-shrink-0')}
            </div>
            {customer.email && <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{customer.email}</div>}
            {customer.phone && <div className="text-muted small"><i className="feather-phone me-1"></i>{customer.phone}</div>}
            {(customer.address || customer.city) && (
              <div className="text-muted small text-truncate">
                <i className="feather-map-pin me-1"></i>{[customer.address, customer.postalCode, customer.city].filter(Boolean).join(', ')}
              </div>
            )}
            {customer.notes && <div className="text-muted small text-truncate mt-1"><i className="feather-file-text me-1"></i>{customer.notes}</div>}
          </div>
          <div className="d-flex gap-1 flex-shrink-0 ms-2">
            <button
              type="button"
              className="btn btn-sm btn-light-primary admin-list-card__open"
              onClick={(e) => { e.stopPropagation(); open(); }}
              title={t('customers.viewCustomer')}
              aria-label={t('customers.viewCustomer')}
            >
              <i className="feather-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('customers.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('customers.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('customers.description')}</p>
        </div>
      </div>

      <Card>
        <ProjectsListTable
          rows={customers}
          columns={columns}
          rowKey={c => c.id}
          columnOrder={columnOrder}
          onColumnReorder={handleColumnReorder}
          reorderTitle={t('customers.dragToReorder')}
          initialSortField="customerNumber"
          initialSortDirection="asc"
          initialFilters={initialFilters}
          onOpen={openCustomer}
          onSelect={toggleSelected}
          selectedKey={selectedCustomerId}
          actionTitle={t('customers.viewCustomer')}
          emptyText={t('customers.emptyList')}
          noResultsText={t('customers.noResults')}
          rangeText={range => t('customers.showingRange', range)}
          mobileFilter={{
            searchField: 'name',
            select: { field: 'verifiedCustomer', allLabel: t('customers.allStatuses'), options: statusOptions },
            sort: {
              label: t('projects.sort.label'),
              options: [
                { field: 'customerNumber', direction: 'asc', label: t('customers.sort.customerNumber') },
                { field: 'name', direction: 'asc', label: t('customers.sort.nameAZ') }
              ]
            }
          }}
          renderMobileCard={renderMobileCard}
          onVisibleRowsChange={setVisibleRows}
          testIds={{ table: 'customer-list-table', cards: 'customer-list-cards' }}
          toolbar={
            <>
              <Button className="admin-list-toolbar__btn" onClick={() => navigate('/customers/invite')} aria-label={t('customers.inviteCustomer')}>
                <i className="feather-user-plus" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.inviteCustomer')}</span>
              </Button>
              <Button
                variant="light-brand"
                className="admin-list-toolbar__btn"
                onClick={() => selectedCustomer && openCustomer(selectedCustomer)}
                disabled={!selectedCustomer}
                aria-label={t('customers.viewCustomer')}
              >
                <i className="feather-eye" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.viewCustomer')}</span>
              </Button>
              <Button variant="light-brand" className="admin-list-toolbar__btn" onClick={exportToExcel} aria-label={t('customers.export')}>
                <i className="feather-download" aria-hidden="true"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.export')}</span>
              </Button>
            </>
          }
        />
      </Card>
    </DashboardLayout>
  );
}
